/**
 * Dispositions — the ranking that decides which call a borrower's row is built from.
 *
 * CONTRACT §5 in three rules, and this screen exists because of the third:
 *
 * 1. **Rank contact outcomes only.** A borrower with nothing but RNR/BUSY/FAILED comes back
 *    `Not contacted`. Failures are not ranked against each other, so they live in their own
 *    list here rather than at the bottom of the good one.
 *
 * 2. **Display every value exactly as written.** Matching is case-folded — `MAIN_MESSAGE` and
 *    `Main_Message` are the same outcome — but nothing on this screen case-corrects, expands
 *    an abbreviation or fixes a spelling. A misspelled disposition is the outcome the agent
 *    actually reported; "correcting" it silently unranks every row that carries it.
 *
 * 3. **A value we do not know is surfaced, not guessed.** An unranked disposition is never
 *    quietly sorted last — the rollup collects it into `run.unranked_dispositions` and it
 *    arrives here as an explicit decision to make. `MAIN_MESSAGE` is the live example: whether
 *    a message played to voicemail counts as a person reached is a commercial call, not a
 *    code one, and until someone makes it those rows are ranked by nothing at all.
 *
 * CONTRACT-FLOWS adds the fourth: **the ladder belongs to the flow, not the client.** A client
 * may run several campaigns under one prefix and the meaning of a disposition depends on
 * which, so each flow can carry its own ranking here — or declare that it has no best outcome
 * at all (a one-way broadcast, a message-delivery call), in which case its borrowers read
 * `Not ranked`: not `Not contacted`, which would be a false claim about a call that connected,
 * and not `Unclassified`, which means a decision is still owed. Without this section that
 * configuration would be editable only by redeploying a JSON file.
 *
 * Reordering is drag-and-drop, and also two buttons on every row: a list that can only be
 * reordered by dragging is a list that cannot be reordered by keyboard.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Badge from '@/components/Badge';
import Button from '@/components/Button';
import ConfirmDialog from '@/components/ConfirmDialog';
import EmptyState from '@/components/EmptyState';
import { Input, Select, Textarea } from '@/components/Field';
import { Card, PageHeader } from '@/components/Layout';
import Table, { type Column } from '@/components/Table';
import { useToast } from '@/components/Toast';
import { api, errorMessage } from '@/lib/api';
import { formatCount, istDateTime, ordinal, pluralise, timeAgo } from '@/lib/format';
import { NOT_CONTACTED, NOT_RANKED } from '@/lib/types';
import type {
  Client,
  DispositionConfig,
  DispositionPatch,
  FlowConfig,
  FlowMap,
  FlowRankingView,
} from '@/lib/types';

/** The API adds a couple of fields beyond the wire type (services/api/dispositions.py). */
interface UnrankedValue {
  value: string;
  seen_count?: number;
  run_count?: number;
  first_seen_at?: string;
  last_seen_at?: string;
}

interface DispositionPayload extends Omit<DispositionConfig, 'unranked'> {
  client_name?: string;
  unranked: UnrankedValue[];
}

// Defensive on purpose: this is fed straight from the API, and a single unexpected
// entry should not cost the operator the whole ranking screen.
const fold = (value: unknown): string => String(value ?? '').trim().toLowerCase();

/** `'client'` edits the client-level lists; anything else is a flow name, verbatim. */
type Target = string;
const CLIENT_TARGET: Target = 'client';

/**
 * The API returns each flow enriched with what the fold *resolves* for it; the PATCH takes
 * only what is stored. Dropping the derived half here is what keeps "inherits the client
 * ranking" from being silently frozen into a copy of it on the next save.
 */
function storedFlows(views: FlowRankingView[] | undefined): FlowMap {
  const out: FlowMap = {};
  for (const view of views ?? []) {
    if (!view || typeof view.flow !== 'string') continue;
    if (view.ranked === false || view.ranking === 'none') {
      out[view.flow] = { ranking: 'none', reason: view.reason ?? '' };
      continue;
    }
    const entry: FlowConfig = {};
    if ((view.disposition_ranks ?? []).length > 0) entry.disposition_ranks = [...view.disposition_ranks];
    if ((view.non_contact ?? []).length > 0) entry.non_contact = [...view.non_contact];
    if (view.reason) entry.reason = view.reason;
    out[view.flow] = entry;
  }
  return out;
}

/** Stable text for a dirty check. Key order is preserved, because it is the display order. */
const flowsKey = (flows: FlowMap): string =>
  JSON.stringify(
    Object.entries(flows).map(([name, entry]) => [
      name,
      entry.ranking ?? 'ranked',
      entry.reason ?? '',
      entry.disposition_ranks ?? [],
      entry.non_contact ?? [],
    ]),
  );

const isUnrankedFlow = (entry: FlowConfig | undefined): boolean => entry?.ranking === 'none';

// ---------------------------------------------------------------------------
// Ranked list — drag and keyboard
// ---------------------------------------------------------------------------

interface RankListProps {
  values: string[];
  onChange: (next: string[]) => void;
  onDemote: (value: string) => void;
  onRemove: (value: string) => void;
  disabled?: boolean;
  label?: string;
  emptyTitle?: string;
  emptyDescription?: string;
}

function RankList({
  values,
  onChange,
  onDemote,
  onRemove,
  disabled = false,
  label = 'Ranked contact outcomes, best first',
  emptyTitle = 'Nothing is ranked',
  emptyDescription = 'With an empty rank list every borrower on this client resolves to “Not contacted”, whatever happened on the call. Promote the outcomes below that mean a person was reached.',
}: RankListProps) {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  const move = (from: number, to: number): void => {
    if (from === to || from < 0 || to < 0 || from >= values.length || to >= values.length) return;
    const next = [...values];
    const [moved] = next.splice(from, 1);
    if (moved === undefined) return;
    next.splice(to, 0, moved);
    onChange(next);
  };

  if (values.length === 0) {
    return <EmptyState tone="danger" title={emptyTitle} description={emptyDescription} />;
  }

  return (
    <ol
      className="stack-tight"
      style={{ listStyle: 'none', margin: 0, padding: 0 }}
      aria-label={label}
    >
      {values.map((value, index) => {
        const isDragging = dragIndex === index;
        const isOver = overIndex === index && dragIndex !== null && dragIndex !== index;
        const dropAbove = isOver && (dragIndex ?? 0) > index;

        return (
          <li
            key={value}
            draggable={!disabled}
            onDragStart={(event) => {
              if (disabled) return;
              setDragIndex(index);
              event.dataTransfer.effectAllowed = 'move';
              // Firefox refuses to start a drag without payload.
              event.dataTransfer.setData('text/plain', String(index));
            }}
            onDragOver={(event) => {
              if (disabled || dragIndex === null) return;
              event.preventDefault();
              event.dataTransfer.dropEffect = 'move';
              setOverIndex(index);
            }}
            onDrop={(event) => {
              if (disabled || dragIndex === null) return;
              event.preventDefault();
              move(dragIndex, index);
              setDragIndex(null);
              setOverIndex(null);
            }}
            onDragEnd={() => {
              setDragIndex(null);
              setOverIndex(null);
            }}
            style={{
              // ~30px. The whole meaning of this list is the ORDER, and a 20-outcome
              // ladder at 46px a row is ~920px — taller than any laptop viewport, so the
              // one comparison the screen exists to support cannot be made without
              // scrolling between the two rows being compared.
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--s-2)',
              minHeight: 30,
              padding: '0 var(--s-3)',
              borderRadius: 'var(--r-2)',
              border: '1px solid var(--line)',
              borderTop: isOver && dropAbove ? '2px solid var(--accent)' : '1px solid var(--line)',
              borderBottom:
                isOver && !dropAbove ? '2px solid var(--accent)' : '1px solid var(--line)',
              background: index === 0 ? 'var(--ok-soft)' : 'var(--surface)',
              opacity: isDragging ? 0.45 : 1,
              cursor: disabled ? 'default' : 'grab',
            }}
          >
            <span className="mono dim" aria-hidden="true" title="Drag to reorder">
              ⠿
            </span>
            <span
              className="mono num"
              style={{ minWidth: 26, textAlign: 'right', color: 'var(--text-3)' }}
            >
              {index + 1}
            </span>
            {/* Verbatim. No case-correcting, no expansion — CONTRACT §5. */}
            <span className="mono strong truncate" style={{ minWidth: 0 }}>
              {value}
            </span>
            {index === 0 ? (
              <Badge tone="ok" glyph="★" title="Best outcome — the winning call is chosen by this">
                best
              </Badge>
            ) : null}

            <span className="grow" />

            <span className="sr-only">{`${value} is ranked ${ordinal(index + 1)} of ${values.length}`}</span>

            <Button
              size="sm"
              variant="ghost"
              iconOnly
              icon={<span aria-hidden="true">↑</span>}
              disabled={disabled || index === 0}
              title={`Move ${value} up`}
              aria-label={`Move ${value} up, currently ${ordinal(index + 1)} of ${values.length}`}
              onClick={() => move(index, index - 1)}
            />
            <Button
              size="sm"
              variant="ghost"
              iconOnly
              icon={<span aria-hidden="true">↓</span>}
              disabled={disabled || index === values.length - 1}
              title={`Move ${value} down`}
              aria-label={`Move ${value} down, currently ${ordinal(index + 1)} of ${values.length}`}
              onClick={() => move(index, index + 1)}
            />
            <Button
              size="sm"
              variant="ghost"
              iconOnly
              icon={<span aria-hidden="true">⤓</span>}
              disabled={disabled}
              title="This outcome does not mean a person was reached"
              aria-label={`${value} is not a contact outcome`}
              onClick={() => onDemote(value)}
            />
            <Button
              size="sm"
              variant="ghost"
              iconOnly
              icon={<span aria-hidden="true">✕</span>}
              disabled={disabled}
              title={`Remove ${value} from the ranking`}
              aria-label={`Remove ${value} from the ranking`}
              onClick={() => onRemove(value)}
            />
          </li>
        );
      })}
    </ol>
  );
}

// ---------------------------------------------------------------------------
// Non-contact chips
// ---------------------------------------------------------------------------

interface NonContactListProps {
  values: string[];
  onPromote: (value: string) => void;
  onForget: (value: string) => void;
  disabled?: boolean;
  emptyNote: string;
}

function NonContactList({ values, onPromote, onForget, disabled, emptyNote }: NonContactListProps) {
  if (values.length === 0) return <p className="muted small">{emptyNote}</p>;
  return (
    // A reference list, unordered by design and read-only almost every time it is looked
    // at. The value reads as text and its two controls are icon-sized, so fifteen
    // declared values stop rendering as a 100px field of buttons competing with the
    // ladder above — which is the thing that actually needs deciding.
    <div className="cluster">
      {values.map((value) => (
        <span
          key={value}
          className="cluster-tight"
          style={{
            padding: '0 var(--s-1) 0 var(--s-2)',
            border: '1px solid var(--line-subtle)',
            borderRadius: 'var(--r-2)',
            background: 'var(--surface-2)',
          }}
        >
          <span className="mono small">
            {value === '' ? <em className="dim">(empty value)</em> : value}
          </span>
          <Button
            size="sm"
            variant="ghost"
            iconOnly
            icon={<span aria-hidden="true">↑</span>}
            disabled={disabled}
            title={`Rank ${value} as a contact outcome instead`}
            aria-label={`Rank ${value} as a contact outcome instead`}
            onClick={() => onPromote(value)}
          />
          <Button
            size="sm"
            variant="ghost"
            iconOnly
            icon={<span aria-hidden="true">✕</span>}
            disabled={disabled}
            title={`Remove ${value} from the non-contact list`}
            aria-label={`Remove ${value} from the non-contact list`}
            onClick={() => onForget(value)}
          />
        </span>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add-an-outcome toolbar
// ---------------------------------------------------------------------------

function AddOutcome({
  id,
  disabled,
  onAdd,
}: {
  id: string;
  disabled?: boolean;
  onAdd: (value: string) => string | null;
}) {
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = (): void => {
    const text = value.trim();
    if (!text) return;
    const problem = onAdd(text);
    if (problem) {
      setError(problem);
      return;
    }
    setValue('');
    setError(null);
  };

  return (
    <>
      <div className="toolbar">
        <label className="xs dim" htmlFor={id}>
          Add an outcome
        </label>
        <Input
          id={id}
          size="sm"
          mono
          spellCheck={false}
          autoComplete="off"
          placeholder="exactly as the source spells it"
          value={value}
          disabled={disabled}
          onChange={(event) => {
            setValue(event.target.value);
            setError(null);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              submit();
            }
          }}
          style={{ maxWidth: 280 }}
        />
        <Button size="sm" disabled={disabled || !value.trim()} onClick={submit}>
          Add to ranking
        </Button>
        <span className="grow" />
        <span className="xs dim">
          Typed verbatim. A misspelling in the export is the outcome the agent reported — match
          it, do not fix it.
        </span>
      </div>

      {error ? (
        <div className="callout tone-danger" role="alert">
          <span className="callout-glyph" aria-hidden="true">
            ✕
          </span>
          <span>{error}</span>
        </div>
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------

export default function Dispositions() {
  const params = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const clientId = params.clientId ?? '';

  const [clients, setClients] = useState<Client[]>([]);
  const [clientsError, setClientsError] = useState<string | null>(null);

  const [config, setConfig] = useState<DispositionPayload | null>(null);
  const [ranks, setRanks] = useState<string[]>([]);
  const [nonContact, setNonContact] = useState<string[]>([]);
  const [flows, setFlows] = useState<FlowMap>({});
  const [openFlow, setOpenFlow] = useState<string | null>(null);
  const [target, setTarget] = useState<Target>(CLIENT_TARGET);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [removingFlow, setRemovingFlow] = useState<string | null>(null);
  const [newFlow, setNewFlow] = useState('');
  const [flowError, setFlowError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.clients
      .listAll()
      .then((items) => {
        if (!cancelled) setClients(items);
      })
      .catch((err: unknown) => {
        if (!cancelled) setClientsError(errorMessage(err));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(async (): Promise<void> => {
    if (!clientId) {
      setConfig(null);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const payload = (await api.dispositions.get(clientId)) as unknown as DispositionPayload;
      setConfig(payload);
      setRanks([...(payload.disposition_ranks ?? [])]);
      setNonContact([...(payload.non_contact ?? [])]);
      setFlows(storedFlows(payload.flows));
      setOpenFlow(null);
      setTarget(CLIENT_TARGET);
    } catch (err) {
      setLoadError(errorMessage(err));
      setConfig(null);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    void load();
  }, [load]);

  const client = useMemo(
    () => clients.find((c) => c.client_id === clientId) ?? null,
    [clients, clientId],
  );

  const storedFlowMap = useMemo(() => storedFlows(config?.flows), [config]);

  const dirty = useMemo(() => {
    if (!config) return false;
    return (
      JSON.stringify(ranks) !== JSON.stringify(config.disposition_ranks ?? []) ||
      JSON.stringify(nonContact) !== JSON.stringify(config.non_contact ?? []) ||
      flowsKey(flows) !== flowsKey(storedFlowMap)
    );
  }, [config, ranks, nonContact, flows, storedFlowMap]);

  const flowNames = useMemo(() => Object.keys(flows), [flows]);

  /**
   * Why one flow blocks the save, per flow. The page-level callout named the flow but
   * nothing on the flow's own row did, so it had to be found by matching the name in the
   * callout against the list.
   */
  const flowProblem = useMemo<Record<string, string | null>>(() => {
    const out: Record<string, string | null> = {};
    for (const [name, entry] of Object.entries(flows)) {
      if (isUnrankedFlow(entry) && !String(entry.reason ?? '').trim()) {
        out[name] = `${name} declares no best outcome but has no reason. It is what the report and this screen show in place of one.`;
      } else if (
        !isUnrankedFlow(entry) &&
        (entry.disposition_ranks ?? []).length === 0 &&
        ranks.length === 0
      ) {
        out[name] = `${name} ranks nothing and there is no client ranking to fall back on, so every borrower in it would read “${NOT_CONTACTED}”.`;
      } else {
        out[name] = null;
      }
    }
    return out;
  }, [flows, ranks]);

  /** A `ranking: "none"` flow with no reason is refused by the API — say so before the save. */
  const blockingProblem = useMemo(() => {
    for (const [name, entry] of Object.entries(flows)) {
      if (isUnrankedFlow(entry) && !String(entry.reason ?? '').trim()) {
        return `${name} declares no best outcome but has no reason. It is what the report and this screen show in place of one.`;
      }
      if (!isUnrankedFlow(entry) && (entry.disposition_ranks ?? []).length === 0 && ranks.length === 0) {
        return `${name} ranks nothing and there is no client ranking to fall back on, so every borrower in it would read “${NOT_CONTACTED}”.`;
      }
    }
    return null;
  }, [flows, ranks]);

  /**
   * Server-side unranked, minus anything the operator has already decided in this draft —
   * in any ladder. The list is recomputed from run history on every read, so a decision that
   * has not been saved yet would otherwise still be sitting there asking to be made.
   */
  const pending = useMemo<UnrankedValue[]>(() => {
    if (!config) return [];
    const decided = new Set([...ranks, ...nonContact].map(fold));
    for (const entry of Object.values(flows)) {
      for (const value of entry.disposition_ranks ?? []) decided.add(fold(value));
      for (const value of entry.non_contact ?? []) decided.add(fold(value));
    }
    return (config.unranked ?? [])
      .map((entry) => (typeof entry === 'string' ? { value: entry } : entry))
      .filter((entry) => entry && entry.value != null && !decided.has(fold(entry.value)));
  }, [config, ranks, nonContact, flows]);

  // ------------------------------------------------------------------ ladders

  /** The two lists a decision applies to, and how to write them back. */
  const ladder = useCallback(
    (which: Target) => {
      if (which === CLIENT_TARGET) {
        return {
          ranks,
          nonContact,
          setRanks,
          setNonContact,
        };
      }
      const entry = flows[which] ?? {};
      return {
        ranks: entry.disposition_ranks ?? [],
        nonContact: entry.non_contact ?? [],
        setRanks: (next: string[]) => patchFlow(which, { disposition_ranks: next }),
        setNonContact: (next: string[]) => patchFlow(which, { non_contact: next }),
      };
    },
    // patchFlow is declared below and is stable for the life of the component.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ranks, nonContact, flows],
  );

  function patchFlow(name: string, patch: Partial<FlowConfig>): void {
    setFlows((current) => {
      const entry = { ...(current[name] ?? {}) };
      for (const [key, value] of Object.entries(patch)) {
        if (Array.isArray(value) && value.length === 0) {
          delete (entry as Record<string, unknown>)[key];
        } else if (value === undefined || value === '') {
          delete (entry as Record<string, unknown>)[key];
        } else {
          (entry as Record<string, unknown>)[key] = value;
        }
      }
      return { ...current, [name]: entry };
    });
  }

  /** CONTRACT §5: two entries differing only in case are one outcome with two ranks. Refused. */
  const collides = (which: Target, value: string): 'ranked' | 'non_contact' | null => {
    const folded = fold(value);
    const lists = ladder(which);
    if (lists.ranks.some((v) => fold(v) === folded)) return 'ranked';
    if (lists.nonContact.some((v) => fold(v) === folded)) return 'non_contact';
    return null;
  };

  const promote = (which: Target, value: string): void => {
    const lists = ladder(which);
    if (collides(which, value) === 'ranked') {
      toast.info(`${value} is already ranked${which === CLIENT_TARGET ? '' : ` in ${which}`}.`);
      return;
    }
    lists.setNonContact(lists.nonContact.filter((v) => fold(v) !== fold(value)));
    lists.setRanks([...lists.ranks, value]);
  };

  const demote = (which: Target, value: string): void => {
    const lists = ladder(which);
    lists.setRanks(lists.ranks.filter((v) => fold(v) !== fold(value)));
    if (!lists.nonContact.some((v) => fold(v) === fold(value))) {
      lists.setNonContact([...lists.nonContact, value]);
    }
  };

  const forget = (which: Target, value: string): void => {
    const lists = ladder(which);
    lists.setRanks(lists.ranks.filter((v) => fold(v) !== fold(value)));
    lists.setNonContact(lists.nonContact.filter((v) => fold(v) !== fold(value)));
  };

  const addOutcome = (which: Target) => (value: string): string | null => {
    const where = collides(which, value);
    if (where) {
      return `${value} is already ${
        where === 'ranked' ? 'in the ranking' : 'declared a non-contact'
      } — matching is case-folded, so it would be the same outcome twice.`;
    }
    const lists = ladder(which);
    lists.setRanks([...lists.ranks, value]);
    return null;
  };

  // ------------------------------------------------------------------ flows

  const addFlow = (): void => {
    const name = newFlow.trim();
    if (!name) return;
    if (flowNames.some((existing) => fold(existing) === fold(name))) {
      setFlowError(
        `${name} is already configured — flow matching is case-folded, so one of the two would never be reached.`,
      );
      return;
    }
    setFlows((current) => ({ ...current, [name]: {} }));
    setOpenFlow(name);
    setNewFlow('');
    setFlowError(null);
  };

  const removeFlow = (name: string): void => {
    setFlows((current) => {
      const next = { ...current };
      delete next[name];
      return next;
    });
    if (openFlow === name) setOpenFlow(null);
    if (target === name) setTarget(CLIENT_TARGET);
  };

  const setFlowRanking = (name: string, ranking: 'ranked' | 'none'): void => {
    setFlows((current) => {
      const entry = current[name] ?? {};
      if (ranking === 'none') {
        return { ...current, [name]: { ranking: 'none', reason: entry.reason ?? '' } };
      }
      const next: FlowConfig = {};
      if (entry.reason) next.reason = entry.reason;
      return { ...current, [name]: next };
    });
  };

  const copyClientRanking = (name: string): void => {
    setFlows((current) => ({
      ...current,
      [name]: {
        ...(current[name] ?? {}),
        disposition_ranks: [...ranks],
        ...(nonContact.length > 0 ? { non_contact: [...nonContact] } : {}),
      },
    }));
  };

  // ------------------------------------------------------------------ save

  const save = async (): Promise<void> => {
    if (!clientId || !dirty || blockingProblem) return;
    setSaving(true);
    try {
      const body: DispositionPatch = {
        disposition_ranks: ranks,
        non_contact: nonContact,
      };
      // Only when it changed. A client that has never had flows must not acquire an empty
      // `flows` attribute just because this screen rendered.
      if (flowsKey(flows) !== flowsKey(storedFlowMap)) body.flows = flows;

      const written = (await api.dispositions.patch(clientId, body)) as unknown as DispositionPayload;
      setConfig(written);
      setRanks([...(written.disposition_ranks ?? [])]);
      setNonContact([...(written.non_contact ?? [])]);
      setFlows(storedFlows(written.flows));
      toast.success(`Ranking saved for ${clientId}.`, {
        description: `${pluralise(written.disposition_ranks?.length ?? 0, 'ranked outcome')}${
          (written.flows ?? []).length > 0
            ? ` · ${pluralise((written.flows ?? []).length, 'flow')}`
            : ''
        } · applies to the next run, not to reports already sent.`,
      });
    } catch (err) {
      toast.error(errorMessage(err), { duration: 0 });
    } finally {
      setSaving(false);
    }
  };

  // ------------------------------------------------------------------ picker

  const picker = (
    // Navigation, not an action: changing it navigates away and takes any unsaved
    // ranking edits with it. It sat in `page-actions` beside Discard and Save, at full
    // width, which made the widest control in the header the one that throws work away.
    <Select
      size="sm"
      className="select-auto"
      aria-label="Client"
      value={clientId}
      onChange={(event) => {
        const next = event.target.value;
        navigate(next ? `/dispositions/${encodeURIComponent(next)}` : '/dispositions');
      }}
    >
      <option value="">Choose a client…</option>
      {clients.map((c) => (
        <option key={c.client_id} value={c.client_id}>
          {c.client_id} — {c.company_name || c.name}
        </option>
      ))}
    </Select>
  );

  if (!clientId) {
    return (
      <>
        <PageHeader
          title="Dispositions"
          subtitle="Contact-outcome ranking, per client and per flow. It decides which call a borrower's report row is built from."
          actions={picker}
        />
        <Card title="Pick a client">
          {clientsError ? (
            <EmptyState
              tone="danger"
              title="Could not load the client list"
              description={clientsError}
              action={
                <Button variant="primary" onClick={() => window.location.reload()}>
                  Reload
                </Button>
              }
            />
          ) : clients.length === 0 ? (
            <EmptyState
              title="No clients configured"
              description="Ranking is per client, so there is nothing to rank yet."
              action={
                <Button variant="primary" onClick={() => navigate('/clients')}>
                  Go to clients
                </Button>
              }
            />
          ) : (
            <div className="stack-tight">
              {clients.map((c) => (
                <button
                  key={c.client_id}
                  type="button"
                  className="btn"
                  style={{ justifyContent: 'flex-start' }}
                  onClick={() => navigate(`/dispositions/${encodeURIComponent(c.client_id)}`)}
                >
                  <span className="mono strong">{c.client_id}</span>
                  <span className="dim">— {c.company_name || c.name}</span>
                  <span className="grow" />
                  <span className="xs dim">
                    {pluralise((c.disposition_ranks ?? []).length, 'ranked outcome')}
                    {Object.keys(c.flows ?? {}).length > 0
                      ? ` · ${pluralise(Object.keys(c.flows ?? {}).length, 'flow')}`
                      : ''}
                  </span>
                </button>
              ))}
            </div>
          )}
        </Card>
      </>
    );
  }

  const flowColumn = config?.flow_column ?? '';

  return (
    <>
      <PageHeader
        breadcrumbs={[
          { label: 'Clients', to: '/clients' },
          { label: clientId, to: `/clients/${encodeURIComponent(clientId)}` },
          { label: 'Dispositions' },
        ]}
        title="Disposition ranking"
        subtitle={
          <span>
            <code className="mono">{clientId}</code>
            {client?.company_name ? ` · ${client.company_name}` : ''} · best outcome first.
            Matching is case-folded; the text is stored and shown exactly as written.
          </span>
        }
        actions={
          <>
            <Button disabled={!dirty || saving} onClick={() => setDiscarding(true)}>
              Discard
            </Button>
            <Button
              variant="primary"
              loading={saving}
              disabled={!dirty || Boolean(blockingProblem)}
              title={blockingProblem ?? undefined}
              onClick={() => void save()}
            >
              {dirty ? 'Save ranking' : 'Saved'}
            </Button>
          </>
        }
      />

      <div className="toolbar">
        <span className="small dim">Client</span>
        {picker}
        <span className="grow" />
        <span className="xs dim">
          Changing the client leaves this screen — unsaved ranking edits go with it.
        </span>
      </div>

      {loadError ? (
        <Card>
          <EmptyState
            tone="danger"
            title="Could not load the ranking"
            description={loadError}
            action={
              <Button variant="primary" onClick={() => void load()}>
                Retry
              </Button>
            }
          />
        </Card>
      ) : null}

      {dirty ? (
        <div className="callout tone-warn">
          <span className="callout-glyph" aria-hidden="true">
            ▲
          </span>
          <span>
            Unsaved ranking changes. They apply to the <strong>next</strong> run — reports already
            sent were built with the ranking that was stored at the time and are not rewritten.
          </span>
        </div>
      ) : null}

      {blockingProblem ? (
        <div className="callout tone-danger" role="alert">
          <span className="callout-glyph" aria-hidden="true">
            ✕
          </span>
          <span>{blockingProblem}</span>
        </div>
      ) : null}

      {/* -------------------------------------------------------- needs a decision */}
      {pending.length > 0 ? (
        <Card
          title={`Needs a decision · ${pluralise(pending.length, 'outcome')}`}
          actions={
            <span className="xs dim">
              Seen in recent runs, in neither list. Not sorted last — undecided.
            </span>
          }
          flush
        >
          {/* The ladder a decision writes to, on a full-width line above the rows rather
              than a Select in the far-right of the card head. With several flows
              configured, nothing in the operator's eye-line at the moment of the click
              said which ladder was about to change — the target was reachable only
              through each button's title attribute. */}
          {flowNames.length > 0 ? (
            <div className="toolbar" style={{ border: 0, borderRadius: 0 }}>
              <label className="small" htmlFor="nrd-decision-target">
                Decisions below apply to:
              </label>
              <Select
                id="nrd-decision-target"
                size="sm"
                className="select-auto"
                value={target}
                onChange={(event) => setTarget(event.target.value)}
              >
                <option value={CLIENT_TARGET}>Client ranking (every unnamed flow)</option>
                {flowNames
                  .filter((name) => !isUnrankedFlow(flows[name]))
                  .map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
              </Select>
            </div>
          ) : null}
          <Table
            columns={
              [
                {
                  key: 'value',
                  header: 'Disposition',
                  render: (row) => (
                    <div className="cluster-tight">
                      {/* Verbatim — including whatever the source spelled it as. */}
                      <span className="mono strong">{row.value}</span>
                      <Badge tone="warn" glyph="?" pending title="In neither list">
                        unranked
                      </Badge>
                    </div>
                  ),
                },
                {
                  key: 'seen',
                  header: 'Rows seen',
                  width: '110px',
                  align: 'right',
                  mono: true,
                  render: (row) => formatCount(row.seen_count ?? 0),
                },
                {
                  key: 'runs',
                  header: 'Runs',
                  width: '80px',
                  align: 'right',
                  mono: true,
                  render: (row) => formatCount(row.run_count ?? 0),
                },
                {
                  key: 'last',
                  header: 'Last seen (IST)',
                  width: '190px',
                  nowrap: true,
                  render: (row) =>
                    row.last_seen_at ? (
                      <span title={istDateTime(row.last_seen_at)}>
                        {istDateTime(row.last_seen_at)}{' '}
                        <span className="xs dim">({timeAgo(row.last_seen_at)})</span>
                      </span>
                    ) : (
                      <span className="dim">—</span>
                    ),
                },
                {
                  key: 'decide',
                  header: 'Decision',
                  width: '300px',
                  align: 'right',
                  nowrap: true,
                  render: (row) => (
                    <div className="cluster-tight" style={{ justifyContent: 'flex-end' }}>
                      <Button
                        size="sm"
                        variant="primary"
                        title={`A person was reached — rank it in ${
                          target === CLIENT_TARGET ? 'the client ranking' : target
                        }, then drag it into position`}
                        onClick={() => promote(target, row.value)}
                      >
                        {target === CLIENT_TARGET ? 'Rank as contact' : `Rank in ${target}`}
                      </Button>
                      <Button
                        size="sm"
                        title="Nobody was reached — never ranked against other failures"
                        onClick={() => demote(target, row.value)}
                      >
                        Not a contact
                      </Button>
                    </div>
                  ),
                },
              ] satisfies Column<UnrankedValue>[]
            }
            rows={pending}
            rowKey={(row) => row.value}
            dense
            flush
            rowTone={() => 'warn'}
            caption="Dispositions seen in recent runs with no ranking decision"
          />
        </Card>
      ) : null}

      {/* -------------------------------------------------------- flows */}
      <Card
        title={`Flows · ${flowNames.length === 0 ? 'one ranking for the whole client' : formatCount(flowNames.length)}`}
        actions={
          <span className="xs dim">
            {flowColumn ? (
              <>
                Matched on <code className="mono">{flowColumn}</code>, case-folded
              </>
            ) : (
              'This client’s field map names no flow column'
            )}
          </span>
        }
      >
        <div className="stack">
          <p className="small">
            A rank index means “third-best outcome <em>in this flow</em>”, so two flows’ indices
            are not comparable and each carries its own ladder. A flow with no entry here falls
            back to the client ranking below. A flow that declares{' '}
            <strong>no best outcome</strong> — a one-way broadcast, or a message-delivery call —
            gives its borrowers <span className="mono">{NOT_RANKED}</span>, which is not{' '}
            <span className="mono">{NOT_CONTACTED}</span>: those calls connected, nobody was ever
            asked a question.
          </p>

          {flowNames.length === 0 ? (
            <p className="muted small">
              No flows configured. Every row is ranked against the client ranking below, whatever
              campaign it came from. Add a flow when one campaign’s vocabulary differs from
              another’s.
            </p>
          ) : (
            <div className="stack-tight">
              {flowNames.map((name) => {
                const entry = flows[name] ?? {};
                const unranked = isUnrankedFlow(entry);
                const ownRanks = entry.disposition_ranks ?? [];
                const ownNonContact = entry.non_contact ?? [];
                const inherits = !unranked && ownRanks.length === 0;
                const isOpen = openFlow === name;
                const problem = flowProblem[name] ?? null;

                return (
                  <div
                    key={name}
                    style={{
                      // An EVEN border in the tone colour — the row's state, without the
                      // banned accent-bar shape. Hue, glyph and words together, so the
                      // blocking flow is findable on its own row instead of only in the
                      // callout at the top of the page.
                      border: `1px solid ${problem ? 'var(--danger-line)' : 'var(--line)'}`,
                      borderRadius: 'var(--r-2)',
                      background: 'var(--surface)',
                    }}
                  >
                    <div
                      className="cluster"
                      style={{ minHeight: 34, padding: '0 var(--s-3)', gap: 'var(--s-2)' }}
                    >
                      <span className="mono strong truncate" style={{ minWidth: 0 }}>
                        {name}
                      </span>
                      {problem ? (
                        <Badge tone="danger" glyph="✕" terminalBad title={problem}>
                          blocks the save
                        </Badge>
                      ) : null}
                      {unranked ? (
                        <Badge tone="neutral" glyph="—" title="No best outcome exists in this flow">
                          {NOT_RANKED}
                        </Badge>
                      ) : inherits ? (
                        <Badge tone="warn" glyph="↓" title="Falls back to the client ranking">
                          inherits client ranking
                        </Badge>
                      ) : (
                        <Badge tone="ok" glyph="★" title="This flow has a ranking of its own">
                          {pluralise(ownRanks.length, 'ranked outcome')}
                        </Badge>
                      )}
                      {!unranked && ownNonContact.length > 0 ? (
                        <span className="xs dim">
                          {pluralise(ownNonContact.length, 'non-contact')}
                        </span>
                      ) : null}
                      <span className="grow" />
                      <Button
                        size="sm"
                        disabled={saving}
                        aria-expanded={isOpen}
                        onClick={() => setOpenFlow(isOpen ? null : name)}
                      >
                        {isOpen ? 'Close' : 'Edit'}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        iconOnly
                        icon={<span aria-hidden="true">✕</span>}
                        disabled={saving}
                        title={`Remove the ${name} flow`}
                        aria-label={`Remove the ${name} flow`}
                        onClick={() => setRemovingFlow(name)}
                      />
                    </div>

                    {unranked && entry.reason ? (
                      <div
                        className="small dim"
                        style={{ padding: '0 var(--s-3) var(--s-2) var(--s-3)' }}
                      >
                        {entry.reason}
                      </div>
                    ) : null}

                    {isOpen ? (
                      <div
                        className="stack"
                        style={{
                          padding: 'var(--s-3)',
                          borderTop: '1px solid var(--line)',
                          background: 'var(--surface-2)',
                        }}
                      >
                        <div className="cluster-tight">
                          <span className="xs dim">Ranking</span>
                          <Button
                            size="sm"
                            variant={unranked ? 'ghost' : 'primary'}
                            disabled={saving}
                            onClick={() => setFlowRanking(name, 'ranked')}
                          >
                            Rank its outcomes
                          </Button>
                          <Button
                            size="sm"
                            variant={unranked ? 'primary' : 'ghost'}
                            disabled={saving}
                            title={`Borrowers in this flow read “${NOT_RANKED}”`}
                            onClick={() => setFlowRanking(name, 'none')}
                          >
                            No best outcome exists
                          </Button>
                        </div>

                        {unranked ? (
                          <div className="stack-tight">
                            <label className="xs dim" htmlFor={`nrd-flow-reason-${name}`}>
                              Why this flow has no best outcome (required — it is what the report
                              and this screen show in its place)
                            </label>
                            <Textarea
                              id={`nrd-flow-reason-${name}`}
                              rows={3}
                              spellCheck
                              disabled={saving}
                              placeholder="one-way broadcast that writes no disposition, ever — nobody was asked a question"
                              value={entry.reason ?? ''}
                              onChange={(event) =>
                                setFlows((current) => ({
                                  ...current,
                                  [name]: { ranking: 'none', reason: event.target.value },
                                }))
                              }
                            />
                            <p className="xs dim">
                              The borrower report for this flow leads on delivery instead:
                              attempts, connected, talk time, last attempt.
                            </p>
                          </div>
                        ) : (
                          <div className="stack">
                            {inherits ? (
                              <div className="cluster">
                                <span className="small">
                                  No ranking of its own — rows in this flow use the client ranking
                                  below.
                                </span>
                                <Button
                                  size="sm"
                                  disabled={saving || ranks.length === 0}
                                  onClick={() => copyClientRanking(name)}
                                >
                                  Start from the client ranking
                                </Button>
                              </div>
                            ) : null}

                            <RankList
                              values={ownRanks}
                              onChange={(next) => patchFlow(name, { disposition_ranks: next })}
                              onDemote={(value) => demote(name, value)}
                              onRemove={(value) => forget(name, value)}
                              disabled={saving}
                              label={`Ranked contact outcomes for ${name}, best first`}
                              emptyTitle="This flow ranks nothing of its own"
                              emptyDescription="Rows in it are scored against the client ranking below. Add an outcome to give this flow its own ladder — a rank index only means anything inside one flow."
                            />

                            <AddOutcome
                              id={`nrd-flow-add-${name}`}
                              disabled={saving}
                              onAdd={addOutcome(name)}
                            />

                            <div className="stack-tight">
                              <span className="xs dim">
                                Declared non-contact in this flow
                                {ownNonContact.length === 0 ? ' — inherits the client list' : ''}
                              </span>
                              <NonContactList
                                values={ownNonContact}
                                onPromote={(value) => promote(name, value)}
                                onForget={(value) => forget(name, value)}
                                disabled={saving}
                                emptyNote="Nothing declared for this flow; the client-level non-contact list applies."
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}

          <div className="toolbar">
            <label className="xs dim" htmlFor="nrd-flow-add">
              Add a flow
            </label>
            <Input
              id="nrd-flow-add"
              size="sm"
              mono
              spellCheck={false}
              autoComplete="off"
              placeholder="exactly as the source spells it"
              value={newFlow}
              disabled={saving}
              onChange={(event) => {
                setNewFlow(event.target.value);
                setFlowError(null);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  addFlow();
                }
              }}
              style={{ maxWidth: 280 }}
            />
            <Button size="sm" disabled={saving || !newFlow.trim()} onClick={addFlow}>
              Add flow
            </Button>
            <span className="grow" />
            <span className="xs dim">
              Matched case-folded and trimmed against the source value — spelled, not guessed.
            </span>
          </div>

          {flowError ? (
            <div className="callout tone-danger" role="alert">
              <span className="callout-glyph" aria-hidden="true">
                ✕
              </span>
              <span>{flowError}</span>
            </div>
          ) : null}
        </div>
      </Card>

      {/* -------------------------------------------------------- ranked */}
      <Card
        title={`Contact outcomes · ${formatCount(ranks.length)} ranked`}
        actions={
          <span className="xs dim">
            Drag a row, or use the arrows. Position 1 wins the borrower's report row.
          </span>
        }
      >
        <div className="stack">
          <div className="callout tone-info">
            <span className="callout-glyph" aria-hidden="true">
              i
            </span>
            <span>
              Detail fields — PTP, recording, client extras — come from the{' '}
              <strong>winning</strong> call, the one whose outcome ranks highest, not from the
              latest call. Reordering this list changes which call a borrower's row describes.
              {flowNames.length > 0
                ? ' This is the fallback ladder: a flow with a ranking of its own is scored against that instead.'
                : ''}
            </span>
          </div>

          {loading ? (
            <div className="cluster">
              <span className="spinner" aria-hidden="true" />
              <span className="muted">Loading ranking…</span>
            </div>
          ) : (
            <RankList
              values={ranks}
              onChange={setRanks}
              onDemote={(value) => demote(CLIENT_TARGET, value)}
              onRemove={(value) => forget(CLIENT_TARGET, value)}
              disabled={saving}
            />
          )}

          <AddOutcome
            id="nrd-disposition-add"
            disabled={saving}
            onAdd={addOutcome(CLIENT_TARGET)}
          />
        </div>
      </Card>

      {/* -------------------------------------------------------- non-contact */}
      <Card title={`Declared non-contact · ${formatCount(nonContact.length)}`}>
        <div className="stack">
          <p className="small">
            Unordered on purpose. A borrower whose calls produced only these outcomes gets{' '}
            <span className="mono">{NOT_CONTACTED}</span> — failures are never ranked against each
            other, so there is no “best” RNR.
          </p>

          <NonContactList
            values={nonContact}
            onPromote={(value) => promote(CLIENT_TARGET, value)}
            onForget={(value) => forget(CLIENT_TARGET, value)}
            disabled={saving}
            emptyNote="Nothing declared. Anything the rollup meets that is in neither list arrives above as a decision to make."
          />
        </div>
      </Card>

      {config?.updated_at ? (
        <p className="xs dim">
          Ranking last saved {istDateTime(config.updated_at)}
          {config.updated_by ? ` by ${config.updated_by}` : ''}.
        </p>
      ) : null}

      <ConfirmDialog
        open={discarding}
        tone="warn"
        title="Discard ranking changes?"
        confirmLabel="Discard"
        consequence="The ranking goes back to what is stored, including any decision you made on an unranked outcome and any change to a flow."
        onCancel={() => setDiscarding(false)}
        onConfirm={() => {
          setRanks([...(config?.disposition_ranks ?? [])]);
          setNonContact([...(config?.non_contact ?? [])]);
          setFlows(storedFlows(config?.flows));
          setOpenFlow(null);
          setTarget(CLIENT_TARGET);
          setDiscarding(false);
        }}
      />

      <ConfirmDialog
        open={removingFlow !== null}
        tone="warn"
        title={`Remove the ${removingFlow ?? ''} flow?`}
        confirmLabel="Remove flow"
        consequence={`Rows in ${removingFlow ?? 'it'} go back to being scored against the client ranking. If it was declared to have no best outcome, its borrowers stop reading “${NOT_RANKED}” and start reading whatever that ladder decides. Nothing is saved until you save.`}
        onCancel={() => setRemovingFlow(null)}
        onConfirm={() => {
          if (removingFlow) removeFlow(removingFlow);
          setRemovingFlow(null);
        }}
      />
    </>
  );
}
